import * as React from "react"
import { Button, Box, Stack, Text, Flex } from "@chakra-ui/react"
import { ReactComponent as TripImage } from "../assets/images/trip.svg"
import { ReactComponent as TripInactiveImage } from "../assets/images/trip-inactive.svg"
import { TripEntity } from "../lib/types/trip"
import { formatDuration } from "../lib/helpers/time"
import { PaginationProps } from "./Pagination"
import { LocationLink } from "./LocationLink"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"

dayjs.extend(relativeTime)

interface Props extends PaginationProps {
  trips: TripEntity[]
  selectedTrip?: TripEntity
  onSelectTrip: (trip: TripEntity) => void
}

export const TripsList = (props: Props) => {
  const refs = props.trips.reduce((acc, value) => {
    acc[value.uuid] = React.createRef()
    return acc
  }, {} as { [key: string]: React.RefObject<HTMLDivElement> })

  const groupedTrips = props.trips.reduce((groups, trip) => {
    const date = dayjs(trip.start_date).format("D MMMM YYYY")
    if (groups[date]) {
      groups[date].push(trip)
    } else {
      groups[date] = [trip]
    }
    return groups
  }, {} as { [key: string]: TripEntity[] })

  React.useEffect(() => {
    if (refs && props.selectedTrip && refs[props.selectedTrip.uuid]) {
      refs[props.selectedTrip.uuid].current?.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      })
    }
  }, [props.selectedTrip, refs])

  return (
    <Box>
      {Object.entries(groupedTrips).map(([date, trips]) => (
        <Box key={date} pb={4}>
          <Text
            fontSize="1.1rem"
            color="gray.900"
            fontWeight="600"
            position="sticky"
            top={-1}
            zIndex={10}
            bg="white"
            pb={2}
          >
            {date}
          </Text>
          {trips
            .filter((t) => !!t.route)
            .map((trip, index) => (
              <TripItem
                key={trip.uuid}
                {...{
                  ref: refs[trip.uuid],
                  trip,
                  index,
                  onSelectTrip: props.onSelectTrip,
                  isSelected: trip.uuid === props.selectedTrip?.uuid,
                }}
              />
            ))}
        </Box>
      ))}
      <Flex justify="space-between" align="center" my={4}>
        <Button onClick={() => props.setPage(props.page - 1)} isDisabled={props.page === 1}>
          Prev
        </Button>
        <Button
          onClick={() => props.setPage(props.page + 1)}
          isDisabled={props.page * props.pageSize > (props.count || 0)}
        >
          Next
        </Button>
      </Flex>
    </Box>
  )
}

interface TripItemProps {
  trip: TripEntity
  index: number
  isSelected: boolean
  onSelectTrip: (trip: TripEntity) => void
}

const TripItem = React.forwardRef(function _TripItem(
  { trip, index, isSelected, onSelectTrip }: TripItemProps,
  ref: any,
) {
  const duration = formatDuration(dayjs(trip.end_date).diff(dayjs(trip.start_date), "s"))
  const pointA = trip.route!.coordinates[0]
  const pointB = trip.route!.coordinates[trip.route!.coordinates.length - 1]

  return (
    <div ref={ref}>
      <Button
        h="min-content"
        w="100%"
        textAlign="left"
        alignItems="flex-start"
        onClick={() => onSelectTrip(trip)}
        variant="unstyled"
        bg={index % 2 === 0 ? "gray.50" : "white"}
        _hover={{ bg: "gray.100" }}
        border="2px solid"
        borderColor={isSelected ? "green.500" : "transparent"}
        p={3}
        borderRadius="lg"
        my={2}
      >
        <Stack align="center" direction="row" spacing={2}>
          <Box textAlign="center" px={2}>
            <Text fontWeight="600">{duration.time}</Text>
            <Text color="gray.600" fontSize="0.8rem">
              {duration.unit}
            </Text>
          </Box>
          <Box as={isSelected ? TripImage : TripInactiveImage} w="30px" />
          <Stack px={2} spacing={4}>
            <Stack spacing={0}>
              <Text fontSize="0.9rem"> {dayjs(trip.start_date).format("HH:mm:ss")}</Text>
              <LocationLink obj={{ latitude: pointA[1], longitude: pointA[0] }} maxW="200px" />
            </Stack>
            <Stack spacing={0}>
              <Text fontSize="0.9rem"> {dayjs(trip.end_date).format("HH:mm:ss")}</Text>
              <LocationLink obj={{ latitude: pointB[1], longitude: pointB[0] }} maxW="200px" />
            </Stack>
          </Stack>
        </Stack>
      </Button>
    </div>
  )
})
